import { useMemo } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { BarChart, type BarDatum } from '@/components/charts/BarChart'
import { MetricRow, RecommendationCard, type MetricSpec } from '@/components/cards'
import { List, TransactionRow } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonCard, SkeletonList } from '@/components/states'
import type { CategoryId } from '@/generated/types'
import { api } from '@/lib/api'
import { aggregateUnitState, useProseState } from '@/lib/analysis'
import { t } from '@/lib/copy'
import { categoryName } from '@/lib/display'
import { formatMonth, formatMonthShort, formatMoney } from '@/lib/format'
import { useNavigate, useRoute } from '@/lib/router'
import { categoryColorVar } from '@/lib/tokens'
import { combine, useAsync } from '@/lib/useAsync'

/**
 * One category's trend over time and the transactions behind it.
 *
 * Chart, metrics and rows are deterministic; the only prose is on the related
 * recommendation cards, so `analysisPending` and `analysisFailed` are silent
 * and render exactly as `ready`.
 */
export function CategoryDetailScreen() {
  const route = useRoute()
  const navigate = useNavigate()
  const category = route.params.id as CategoryId

  const cashFlow = useAsync(() => api.analysis.cashFlow(12), [])
  const transactions = useAsync(
    (signal) => api.transactions.list({ category, limit: 25 }, signal),
    [category],
  )
  const recommendations = useAsync(() => api.recommendations.list({ status: 'open' }), [])

  const months = cashFlow.state.data?.months ?? []
  const currency = cashFlow.state.data?.currency ?? 'USD'

  const series = useMemo(
    () =>
      months.map((month) => ({
        month: month.month,
        complete: month.complete !== false,
        amount: Math.abs((month.byCategory ?? []).find((c) => c.category === category)?.amount.amount ?? 0),
      })),
    [months, category],
  )

  const complete = series.filter((s) => s.complete)
  const thisMonth = series.at(-1)?.amount ?? 0
  const average = complete.length ? Math.round(complete.reduce((s, m) => s + m.amount, 0) / complete.length) : 0
  const highest = complete.reduce<{ month: string; amount: number } | null>(
    (best, m) => (!best || m.amount > best.amount ? { month: m.month, amount: m.amount } : best),
    null,
  )

  const loaded = combine(cashFlow.state, transactions.state)

  const dataState =
    loaded.status === 'error'
      ? 'error'
      : loaded.status === 'loading' && !cashFlow.state.data
        ? 'loading'
        : (transactions.state.data?.items.length ?? 0) === 0
          ? 'empty'
          : 'ready'

  const bars: BarDatum[] = series.map((entry) => ({
    label: formatMonthShort(entry.month),
    muted: !entry.complete,
    series: [
      {
        key: 'amount',
        value: entry.amount,
        color: categoryColorVar(category),
        label: categoryName(category),
      },
    ],
  }))

  const metrics: MetricSpec[] = [
    { key: 'this', label: t('categoryDetail.thisMonth'), value: formatMoney({ amount: thisMonth, currency }, { absolute: true }) },
    { key: 'avg', label: t('categoryDetail.monthlyAverage'), value: formatMoney({ amount: average, currency }, { absolute: true }) },
    {
      key: 'high',
      label: t('categoryDetail.highest'),
      value: highest ? formatMoney({ amount: highest.amount, currency }, { absolute: true }) : '—',
      note: highest ? formatMonth(highest.month) : undefined,
    },
  ]

  const related = (recommendations.state.data ?? []).filter((rec) =>
    rec.evidence?.categories?.includes(category),
  )

  const prose = useProseState(
    aggregateUnitState(related.map((rec) => rec.proseState)),
    recommendations.reload,
  )
  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // The three states that render the identical tree below. `analysisPending`
  // adds no spinner and disables nothing; `analysisFailed` is not the error
  // treatment. The only difference between them and `ready` is whether the
  // prose slots hold generated text or their copyKey — and they hold one
  // either way, so the layout does not move when prose arrives.
  // `empty` joins them because this screen's empty treatment is inline: the
  // chart and totals still render, and only the transaction list is replaced.
  const rendersContent =
    screenState === 'ready' ||
    screenState === 'empty' ||
    screenState === 'analysisPending' ||
    screenState === 'analysisFailed'

  return (
    <Screen
      id="categoryDetail"
      state={screenState}
      title={categoryName(category)}
      back={{ screen: 'categories' }}
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonList rows={6} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="categoryDetail.error.title"
          error={loaded.status === 'error' ? loaded.error : undefined}
          onRetry={() => {
            cashFlow.reload()
            transactions.reload()
          }}
        />
      ) : rendersContent ? (
        <div className="stack">
          <div className="card card-pad-lg">
            <BarChart
              data={bars}
              caption={categoryName(category)}
              formatValue={(value) => formatMoney({ amount: value, currency }, { absolute: true })}
            />
          </div>

          <MetricRow metrics={metrics} />

          {related.map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onSelect={() => navigate('recommendationDetail', { id: rec.id })}
            />
          ))}

          <section>
            <SectionHeader title={t('categoryDetail.section.transactions')} />
            {screenState === 'empty' ? (
              <EmptyState titleKey="categoryDetail.empty.title" />
            ) : (
              <List>
                {(transactions.state.data?.items ?? []).map((tx) => (
                  <TransactionRow
                    key={tx.id}
                    transaction={tx}
                    onSelect={() => navigate('transactionDetail', { id: tx.id })}
                  />
                ))}
              </List>
            )}
          </section>
        </div>
      ) : null}
    </Screen>
  )
}
